import { ChampionshipsParticipants } from '../api/model/models'
import { Team } from '../api/model/team'

const compareNumbers = (a?: number | null, b?: number | null) => {
  const first = a ?? Number.MAX_SAFE_INTEGER
  const second = b ?? Number.MAX_SAFE_INTEGER

  if (first !== second) {
    return first - second
  }

  return 0
}

const compareStrings = (a?: string | null, b?: string | null) => (a ?? '').localeCompare(b ?? '', 'ru')

const getRowIdNumber = (id: string | number) => {
  if (typeof id === 'number') {
    return id
  }

  const parsedId = parseInt(id, 10)

  return Number.isNaN(parsedId) ? 0 : parsedId
}

export const transformEvalTeamsData = (teams: Team[]): RatingParticipantsTableData[] => {
  const getCompatriotExpertsLabel = (team: Team) =>
    team.compatriotExperts?.map((expert) => expert.fullName).join(', ') ?? ''
  const getCompatriotExpertIds = (team: Team) => team.compatriotExperts?.map((expert) => expert.id) ?? []
  const getCompatriotExpertLimit = (team: Team) => team.teamParticipants?.length ?? 0
  const canAddCompatriotExpert = (team: Team) =>
    (team.compatriotExperts?.length ?? 0) < (team.teamParticipants?.length ?? 0)

  const transformTeamParticipants = (
    participants: ChampionshipsParticipants[],
    team: Team,
    teamId: string | number,
  ): RatingParticipantsTableData[] => {
    return participants
      .map((participant) => ({
        id: `${participant.id}-${teamId}`,
        name: participant.fullName,
        organization: participant.company,
        region: participant.company,
        compatriotExpert: '',
        compatriotExpertIds: getCompatriotExpertIds(team),
        compatriotExperts: team.compatriotExperts ?? [],
        canAddCompatriotExpert: canAddCompatriotExpert(team),
        compatriotExpertLimit: getCompatriotExpertLimit(team),
        isTeamExpert: participant.isTeamExpert,
        teamId,
      }))
      .sort((a, b) => {
        const nameCompare = compareStrings(a.name, b.name)

        if (nameCompare !== 0) {
          return nameCompare
        }

        return getRowIdNumber(a.id) - getRowIdNumber(b.id)
      })
  }

  const newTableData = teams.map((team) => {
    if (team.teamParticipants && team.teamParticipants.length > 1) {
      const newTeam: RatingParticipantsTableData = {
        id: team.id,
        name: team.teamName ?? team.title ?? (team.teamNumber ? `Команда ${team.teamNumber}` : ''),
        organization: '',
        region: '',
        compatriotExpert: getCompatriotExpertsLabel(team),
        compatriotExpertIds: getCompatriotExpertIds(team),
        compatriotExperts: team.compatriotExperts ?? [],
        canAddCompatriotExpert: canAddCompatriotExpert(team),
        compatriotExpertLimit: getCompatriotExpertLimit(team),
        teamTitle: team.title,
        teamNumber: team.teamNumber,
        children: transformTeamParticipants(team.teamParticipants, team, team.id),
      }

      return newTeam
    }

    if (team.teamParticipants?.length === 1) {
      const newParticipant: RatingParticipantsTableData = {
        id: team.id,
        name: team.teamParticipants[0].fullName,
        organization: team.teamParticipants[0].company,
        region: team.teamParticipants[0].company,
        compatriotExpert: getCompatriotExpertsLabel(team),
        compatriotExpertIds: getCompatriotExpertIds(team),
        compatriotExperts: team.compatriotExperts ?? [],
        canAddCompatriotExpert: canAddCompatriotExpert(team),
        compatriotExpertLimit: getCompatriotExpertLimit(team),
        isTeamExpert: team.teamParticipants[0].isTeamExpert,
      }

      return newParticipant
    }

    return undefined
  })

  return newTableData
    .filter((item): item is RatingParticipantsTableData => !!item)
    .sort((a, b) => {
      const firstIsTeam = !!a.children
      const secondIsTeam = !!b.children

      if (firstIsTeam && !secondIsTeam) {
        return -1
      }

      if (!firstIsTeam && secondIsTeam) {
        return 1
      }

      if (firstIsTeam && secondIsTeam) {
        const teamNumberCompare = compareNumbers(a.teamNumber, b.teamNumber)

        if (teamNumberCompare !== 0) {
          return teamNumberCompare
        }

        const nameCompare = compareStrings(a.name, b.name)

        if (nameCompare !== 0) {
          return nameCompare
        }
      } else {
        const nameCompare = compareStrings(a.name, b.name)

        if (nameCompare !== 0) {
          return nameCompare
        }
      }

      return getRowIdNumber(a.id) - getRowIdNumber(b.id)
    })
}
